import React, { useState, useEffect } from 'react';
import { Moon, Sun } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const Header = () => {
  const logoLight = 'https://download.softhubs.com.br/files/LOGO/soft_logo_black.png';
  const logoDark = 'https://download.softhubs.com.br/files/LOGO/soft_logo_white.png';

  const toggleTheme = () => {
    document.documentElement.classList.toggle('dark');
    const currentTheme = localStorage.getItem('theme');
    localStorage.setItem('theme', currentTheme === 'dark' ? 'light' : 'dark');
  };

  return (
    <header className="bg-white dark:bg-gray-800 shadow-md w-full transition-all duration-300">
      <div className="p-3 flex items-center justify-between">
        <div className="flex flex-col items-center justify-center flex-grow text-center">
          <img
            src={logoLight}
            alt="Logo Light"
            className="w-72 h-auto block dark:hidden cursor-pointer"
            onClick={() => window.location.reload()}
          />
          <img
            src={logoDark}
            alt="Logo Dark"
            className="w-72 h-auto hidden dark:block cursor-pointer"
            onClick={() => window.location.reload()}
          />
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            Análises feito por IA (Innovation).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            aria-label="Toggle theme"
          >
            <Sun className="h-6 w-6 text-gray-600 dark:hidden" />
            <Moon className="h-6 w-6 text-gray-300 hidden dark:block" />
          </button>
        </div>
      </div>
    </header>
  );
};

// Método para revisar o conteúdo retornado pela API
const processContent = (rawContent) => {
  return rawContent
    .split('\n')
    .map(line => (line.includes('>Atendente') && !line.endsWith('  ') ? line + '  ' : line))
    .join('\n');
};

const MarkdownContent = () => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('');
  const [noContentFound, setNoContentFound] = useState(false); // Novo estado
  const [isRefreshing, setIsRefreshing] = useState(false); // Estado para controlar o clique no botão

  useEffect(() => {
    const pathParts = window.location.pathname.split('/');
    const clientId = pathParts[pathParts.length - 1];

    fetch('https://api.softhubs.com.br/query-data?page=1&per_page=5', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-API-Version': '2',
        'Authorization': 'Basic YWRtaW46c3VwZXJzZWNyZXQ='
      },
      body: JSON.stringify({
        database: "dbProjetos",
        fields: ["content_ai", "date_insert"],
        entity: "vw_cs_ai",
        filters: { client_id: clientId }
      })
    })
      .then(res => res.json())
      .then(data => {
        if (data && data.data && data.data.length > 0) {
          const processed = processContent(data.data[0].content_ai);
          setContent(processed);
          setNoContentFound(false); // Conteúdo encontrado
        } else {
          setContent('Nenhum conteúdo encontrado.');
          setNoContentFound(true); // Conteúdo não encontrado
        }
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setContent('Erro ao buscar conteúdo.');
        setLoading(false);
      });
  }, []);

  const generateId = (children) =>
    React.Children.toArray(children).join('').toLowerCase().replace(/\s+/g, '-');

  const getHeadings = () =>
    content.split('\n').filter(line => line.startsWith('#')).map(line => {
      const title = line.replace(/^#+\s/, '');
      return {
        level: line.match(/^#+/)[0].length,
        title,
        id: title.toLowerCase().replace(/\s+/g, '-')
      };
    });

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 70;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      setActiveSection(id);
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '0px 0px -40% 0px', threshold: 0.1 }
    );
    const elements = document.querySelectorAll('h1, h2, h3');
    elements.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [content]);

  // Função para atualizar os dados
  const refreshData = async () => {
    if (isRefreshing) return; // Impede múltiplos cliques

    setIsRefreshing(true); // Inicia o estado de carregamento

    const pathParts = window.location.pathname.split('/');
    const clientId = pathParts[pathParts.length - 1];

    try {
      const response = await fetch(`https://holdx-back.softhubs.com.br/start?client_id=${clientId}`, {
        method: 'POST',
      });

      if (response.ok) {
        // Após atualizar os dados, recarregue a página
        window.location.reload();
      } else {
        alert('Falha ao atualizar os dados. Por favor, tente novamente.');
      }
    } catch (error) {
      console.error('Erro ao atualizar os dados:', error);
      alert('Erro ao atualizar os dados. Verifique sua conexão e tente novamente.');
    } finally {
      setIsRefreshing(false); // Finaliza o estado de carregamento
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200">
      <div className="flex">
        {/* Sumário */}
        <nav className="sticky top-0 w-64 h-screen border-r dark:border-gray-800 overflow-y-auto bg-white dark:bg-gray-900">
          <div className="p-4">
            <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Sumário</h2>
            <div className="space-y-1">
              {getHeadings().map((heading, index) => (
                <button
                  key={index}
                  onClick={() => scrollToSection(heading.id)}
                  className={`w-full text-left py-1.5 text-sm transition-colors ${heading.level === 1 ? 'font-medium' : heading.level === 2 ? 'pl-4' : 'pl-8'} ${activeSection === heading.id ? 'text-blue-600 dark:text-blue-400 font-bold' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}`}
                >
                  {heading.title}
                </button>
              ))}
            </div>
          </div>
        </nav>
        {/* Conteúdo Markdown */}
        <main className="flex-1 p-6">
          {loading ? (
            <p>Carregando conteúdo...</p>
          ) : noContentFound ? (
            <div className="text-center mt-10">
              <p className="text-gray-700 dark:text-gray-300 mb-4">Nenhum conteúdo encontrado.</p>
              <button
                onClick={refreshData}
                disabled={isRefreshing} // Desabilita o botão durante o carregamento
                className={`bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors ${isRefreshing ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isRefreshing ? 'Atualizando...' : 'Atualizar Dados'}
              </button>
            </div>
          ) : (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ node, children, ...props }) => {
                  const id = generateId(children);
                  return (
                    <h1 {...props} id={id} className="text-2xl font-bold mt-5 mb-4 text-gray-900 dark:text-white scroll-mt-[70px]">
                      {children}
                    </h1>
                  );
                },
                h2: ({ node, children, ...props }) => {
                  const id = generateId(children);
                  return (
                    <h2 {...props} id={id} className="text-xl font-semibold mt-4 mb-3 text-gray-900 dark:text-white scroll-mt-[70px]">
                      {children}
                    </h2>
                  );
                },
                h3: ({ node, children, ...props }) => {
                  const id = generateId(children);
                  return (
                    <h3 {...props} id={id} className="text-lg font-medium mt-3 mb-2 text-gray-900 dark:text-white scroll-mt-[70px]">
                      {children}
                    </h3>
                  );
                },
                p: ({ node, children, ...props }) => (
                  <p {...props} className="text-gray-700 dark:text-gray-300 mb-4">{children}</p>
                ),
                ul: ({ node, children, ...props }) => (
                  <ul {...props} className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-300">{children}</ul>
                ),
                ol: ({ node, children, ...props }) => (
                  <ol {...props} className="list-decimal pl-6 mb-4 text-gray-700 dark:text-gray-300">{children}</ol>
                ),
                blockquote: ({ node, children, ...props }) => (
                  <blockquote {...props} className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic text-gray-700 dark:text-gray-300 mb-4">{children}</blockquote>
                ),
                // Adicionando suporte para tabelas com tema escuro
                table: ({ node, children, ...props }) => (
                  <div className="overflow-x-auto mb-4">
                    <table {...props} className="min-w-full border-collapse border border-gray-300 dark:border-gray-600">{children}</table>
                  </div>
                ),
                thead: ({ node, children, ...props }) => (
                  <thead {...props} className="bg-gray-100 dark:bg-gray-700">{children}</thead>
                ),
                tbody: ({ node, children, ...props }) => (
                  <tbody {...props} className="divide-y divide-gray-300 dark:divide-gray-600">{children}</tbody>
                ),
                tr: ({ node, children, ...props }) => (
                  <tr {...props} className="hover:bg-gray-50 dark:hover:bg-gray-800">{children}</tr>
                ),
                th: ({ node, children, ...props }) => (
                  <th {...props} className="px-4 py-2 text-left text-gray-900 dark:text-white font-medium border border-gray-300 dark:border-gray-600">{children}</th>
                ),
                td: ({ node, children, ...props }) => (
                  <td {...props} className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600">{children}</td>
                ),
                // Outros elementos que podem precisar de suporte de tema escuro
                code: ({ node, inline, className, children, ...props }) => (
                  <code
                    className={`${className} ${inline ? 'bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded' : 'block bg-gray-100 dark:bg-gray-800 p-4 rounded overflow-x-auto'} text-gray-800 dark:text-gray-200`}
                    {...props}
                  >
                    {children}
                  </code>
                ),
                a: ({ node, children, ...props }) => (
                  <a {...props} className="text-blue-600 dark:text-blue-400 hover:underline">{children}</a>
                ),
                img: ({ node, ...props }) => (
                  <img {...props} className="max-w-full h-auto rounded my-4" />
                )
              }}
            >
              {content}
            </ReactMarkdown>
          )}
        </main>
      </div>
    </div>
  );
};

const Main = () => {
  return (
    <div>
      <Header />
      <MarkdownContent />
      <footer className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 py-4 px-6 border-t dark:border-gray-700">
        <div className="container mx-auto flex justify-center items-center">
          <p className="text-sm">© 2025 Innovation Softcom. Todos os direitos reservados.</p>
        </div>
      </footer>
    </div>
  );
};

export default Main;
